import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Key, Eye, EyeOff, Check, X, RefreshCw, Shield } from 'lucide-react';
import { API } from '../../App';

// Admin-specific API client that uses admin_token
const adminApi = axios.create({ baseURL: API });
adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const AdminAPIKeys = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [keyStatus, setKeyStatus] = useState(null);
  const [llmStatus, setLlmStatus] = useState(null);
  
  const [openaiKey, setOpenaiKey] = useState('');
  const [anthropicKey, setAnthropicKey] = useState('');
  const [showOpenai, setShowOpenai] = useState(false);
  const [showAnthropic, setShowAnthropic] = useState(false);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      setLoading(true);
      const [keysRes, llmRes] = await Promise.all([
        adminApi.get('/admin/settings/api-keys'),
        adminApi.get('/admin/settings/llm-status')
      ]);
      setKeyStatus(keysRes.data);
      setLlmStatus(llmRes.data);
    } catch (error) {
      toast.error('Failed to load API key status');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const updates = {};
      
      if (openaiKey) {
        updates.openai_api_key = openaiKey;
      }
      if (anthropicKey) {
        updates.anthropic_api_key = anthropicKey;
      }
      
      if (Object.keys(updates).length === 0) {
        toast.error('Enter at least one API key to save');
        return;
      }
      
      const response = await adminApi.put('/admin/settings/api-keys', updates);
      toast.success(response.data.message || 'API keys updated');
      
      // Clear inputs and reload
      setOpenaiKey('');
      setAnthropicKey('');
      await loadStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update API keys');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async (provider) => {
    try {
      setSaving(true);
      const updates = provider === 'openai' 
        ? { openai_api_key: '' }
        : { anthropic_api_key: '' };
      
      await adminApi.put('/admin/settings/api-keys', updates);
      toast.success(`${provider === 'openai' ? 'OpenAI' : 'Anthropic'} key cleared`);
      await loadStatus();
    } catch (error) {
      toast.error('Failed to clear key');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3" />
          <div className="h-32 bg-muted rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">API Keys</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Configure your LLM provider API keys. Keys are stored securely and persist across deployments.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadStatus}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Current Status */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Provider Status
          </CardTitle>
          <CardDescription>
            Current LLM providers and routing configuration
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* OpenAI */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">OpenAI</span>
                {keyStatus?.openai_configured ? (
                  <Badge variant="default" className="bg-green-600">Active</Badge>
                ) : (
                  <Badge variant="secondary">Not Configured</Badge>
                )}
              </div>
              {keyStatus?.openai_key_preview && (
                <p className="text-xs text-muted-foreground font-mono">{keyStatus.openai_key_preview}</p>
              )}
              <p className="text-xs text-muted-foreground mt-2">
                Models: gpt-4.1-mini, gpt-5.2
              </p>
            </div>
            
            {/* Anthropic */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">Anthropic</span>
                {keyStatus?.anthropic_configured ? (
                  <Badge variant="default" className="bg-green-600">Active</Badge>
                ) : (
                  <Badge variant="secondary">Not Configured</Badge>
                )}
              </div>
              {keyStatus?.anthropic_key_preview && (
                <p className="text-xs text-muted-foreground font-mono">{keyStatus.anthropic_key_preview}</p>
              )}
              <p className="text-xs text-muted-foreground mt-2">
                Models: claude-sonnet-4.5
              </p>
            </div>
            
            {/* Emergent */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">Emergent (Fallback)</span>
                {keyStatus?.emergent_configured ? (
                  <Badge variant="default" className="bg-blue-600">Active</Badge>
                ) : (
                  <Badge variant="destructive">Missing</Badge>
                )}
              </div>
              {keyStatus?.emergent_key_preview && (
                <p className="text-xs text-muted-foreground font-mono">{keyStatus.emergent_key_preview}</p>
              )}
              <p className="text-xs text-muted-foreground mt-2">
                Platform-managed fallback
              </p>
            </div>
          </div>

          {/* Routing Info */}
          {llmStatus?.routing_summary && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-sm font-medium mb-2">Current Routing</p>
              <div className="grid grid-cols-3 gap-4 text-xs text-muted-foreground">
                <div>
                  <span className="font-medium text-foreground">Q&A:</span> {llmStatus.routing_summary.questions}
                </div>
                <div>
                  <span className="font-medium text-foreground">Case Review:</span> {llmStatus.routing_summary.case_review}
                </div>
                <div>
                  <span className="font-medium text-foreground">AI Blog:</span> {llmStatus.routing_summary.ai_blog}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Update Keys */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Key className="w-5 h-5" />
            Update API Keys
          </CardTitle>
          <CardDescription>
            Your keys are used first. Emergent fallback activates only if your keys fail or are not set.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* OpenAI */}
          <div className="space-y-2">
            <Label htmlFor="openai">OpenAI API Key</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  id="openai"
                  type={showOpenai ? 'text' : 'password'}
                  placeholder="sk-..."
                  value={openaiKey}
                  onChange={(e) => setOpenaiKey(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowOpenai(!showOpenai)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showOpenai ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {keyStatus?.openai_configured && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleClear('openai')}
                  disabled={saving}
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Get your key from <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="underline">platform.openai.com</a>
            </p>
          </div>

          {/* Anthropic */}
          <div className="space-y-2">
            <Label htmlFor="anthropic">Anthropic API Key</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  id="anthropic"
                  type={showAnthropic ? 'text' : 'password'}
                  placeholder="sk-ant-..."
                  value={anthropicKey}
                  onChange={(e) => setAnthropicKey(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowAnthropic(!showAnthropic)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showAnthropic ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {keyStatus?.anthropic_configured && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleClear('anthropic')}
                  disabled={saving}
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Get your key from <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noopener noreferrer" className="underline">console.anthropic.com</a>
            </p>
          </div>

          <Button onClick={handleSave} disabled={saving || (!openaiKey && !anthropicKey)}>
            {saving ? 'Saving...' : 'Save Keys'}
          </Button>
        </CardContent>
      </Card>

      {/* Usage Stats */}
      {llmStatus?.telemetry && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Usage Statistics</CardTitle>
            <CardDescription>API calls since last restart</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-5 gap-4 text-center">
              <div>
                <p className="text-2xl font-semibold">{llmStatus.telemetry.openai_calls}</p>
                <p className="text-xs text-muted-foreground">OpenAI</p>
              </div>
              <div>
                <p className="text-2xl font-semibold">{llmStatus.telemetry.anthropic_calls}</p>
                <p className="text-xs text-muted-foreground">Anthropic</p>
              </div>
              <div>
                <p className="text-2xl font-semibold">{llmStatus.telemetry.emergent_calls}</p>
                <p className="text-xs text-muted-foreground">Emergent</p>
              </div>
              <div>
                <p className="text-2xl font-semibold">{llmStatus.telemetry.fallbacks}</p>
                <p className="text-xs text-muted-foreground">Fallbacks</p>
              </div>
              <div>
                <p className="text-2xl font-semibold">{llmStatus.telemetry.errors}</p>
                <p className="text-xs text-muted-foreground">Errors</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {keyStatus?.updated_at && (
        <p className="text-xs text-muted-foreground text-center">
          Last updated: {new Date(keyStatus.updated_at).toLocaleString()} by {keyStatus.updated_by}
        </p>
      )}
    </div>
  );
};

export default AdminAPIKeys;
